/**
 * Hook to manage runner data with polling and realtime updates
 * Combines periodic polling with Supabase realtime subscriptions
 *
 * Also fetches events for the live feed display.
 *
 * OPTIMIZATION: Debounced realtime updates to reduce egress (batches rapid events)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { fetchRunnersSummary } from '../lib/queries';
import type { RunnerSummary, FeedEvent } from '../lib/types';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Debounce utility to batch rapid function calls
 * Delays execution until after 'wait' milliseconds have elapsed since the last call
 * 
 * Returns both the debounced function and a cancel function to clear pending timeouts
 * This is critical for React hooks where the debounced function may be recreated,
 * and we need to cancel old pending timeouts to prevent stale closures from firing
 */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): [(...args: Parameters<T>) => void, () => void] {
  let timeout: NodeJS.Timeout | null = null;
  
  const debouncedFn = (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  
  const cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  
  return [debouncedFn, cancel];
}

interface UseRunnerUpdatesOptions {
  /** Whether to enable auto-updates (default: true) */
  enabled?: boolean;
  /**
   * Retained for call-site compatibility (V2 pages pass `includeFeed: false`).
   * The V1 live feed was retired in the merge (Q8) — `fetchEventsFeed` is gone —
   * so this flag no longer triggers any query. `feedEvents` is always empty.
   */
  includeFeed?: boolean;
}

interface UseRunnerUpdatesResult {
  runners: RunnerSummary[];
  /**
   * V1 live-feed events. Retired (Q8) — always an empty array. Kept on the
   * result so the hook's public shape stays stable for any legacy consumer.
   */
  feedEvents: FeedEvent[];
  loading: boolean;
  lastUpdated: Date | null;
  autoRefresh: boolean;
  setAutoRefresh: (enabled: boolean) => void;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch and auto-update runner data
 * @param raceId - The ID of the race to monitor
 * @param options - Configuration options (enabled, includeFeed)
 * @returns Runners data, loading state, last update time, and refresh controls
 */
export function useRunnerUpdates(
  raceId: string | null,
  options: UseRunnerUpdatesOptions | boolean = true
): UseRunnerUpdatesResult {
  // Support both old boolean API and new options object for backward compatibility.
  // `includeFeed` is intentionally NOT destructured — the V1 feed is retired (Q8)
  // and the flag is a no-op, but it stays on the options type so existing callers
  // that pass `{ includeFeed: false }` still type-check.
  const { enabled = true } =
    typeof options === 'boolean' ? { enabled: options } : options;
  const [runners, setRunners] = useState<RunnerSummary[]>([]);
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Use refs to track intervals and channels to prevent duplicate subscriptions
  const pollingIntervalRef = useRef<number | null>(null);
  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);
  const runnersChannelRef = useRef<RealtimeChannel | null>(null);
  // Track debounce cancel function to clear pending timeouts when raceId changes
  const debounceCancelRef = useRef<(() => void) | null>(null);

  /**
   * Reset state when raceId changes to prevent stale data from previous race
   * This fixes the bug where runners from a previous race (with different UUIDs)
   * were being displayed briefly before the new race data loaded
   * 
   * CRITICAL: Also cancel any pending debounce timeouts to prevent old debounced
   * fetches from firing with stale raceId data after switching races
   */
  useEffect(() => {
    // Cancel any pending debounce timeout when raceId changes
    if (debounceCancelRef.current) {
      debounceCancelRef.current();
      debounceCancelRef.current = null;
    }
    
    setRunners([]);
    setFeedEvents([]);
    setLoading(true);
    setLastUpdated(null);
  }, [raceId]);

  /**
   * Fetch runner data from Supabase (immediate version).
   * Memoized to prevent unnecessary re-creation.
   *
   * The V1 live feed was retired in the merge (Q8): `fetchEventsFeed` is deleted,
   * so this only fetches the runner summary. `feedEvents` is left empty — the
   * live view is kept fresh purely by the runner-summary refetch that the
   * checkin_events realtime subscription (below) triggers.
   */
  const fetchRunnersImmediate = useCallback(async () => {
    if (!raceId) return;

    try {
      const runnersData = await fetchRunnersSummary(raceId);
      setRunners(runnersData);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching race data:', err);
      setLoading(false);
    }
  }, [raceId]);

  /**
   * Debounced version for realtime updates (batches rapid events)
   * Waits 2 seconds after last event before fetching
   * This prevents excessive refetches during burst activity (e.g., 10 check-ins in 5 seconds)
   * 
   * CRITICAL: When raceId changes, we must cancel any pending debounce timeout
   * to prevent the old debounced function from firing with stale raceId data.
   * The cancel function is stored in debounceCancelRef and called in cleanup.
   */
  const fetchRunnersDebounced = useMemo(() => {
    // Cancel any existing pending debounce timeout before creating a new one
    // This prevents old timeouts from firing after raceId changes
    if (debounceCancelRef.current) {
      debounceCancelRef.current();
      debounceCancelRef.current = null;
    }
    
    // Create new debounced function for current raceId/fetchRunnersImmediate
    const [debouncedFn, cancel] = debounce(() => {
      fetchRunnersImmediate();
    }, 2000); // 2-second debounce
    
    // Store cancel function so we can clear it on cleanup or raceId change
    debounceCancelRef.current = cancel;
    
    return debouncedFn;
  }, [fetchRunnersImmediate]);

  /**
   * Public refetch function for manual refresh (immediate, not debounced)
   */
  const refetch = useCallback(async () => {
    await fetchRunnersImmediate();
  }, [fetchRunnersImmediate]);

  /**
   * Set up polling and realtime subscriptions
   */
  useEffect(() => {
    if (!raceId || !enabled) {
      setLoading(false);
      return;
    }

    // Initial fetch (immediate)
    fetchRunnersImmediate();

    // Set up polling if auto-refresh is enabled
    // Increased interval from 20s to 60s since realtime is primary update mechanism
    if (autoRefresh) {
      pollingIntervalRef.current = window.setInterval(() => {
        fetchRunnersImmediate();
      }, 60000); // Poll every 60 seconds (realtime handles most updates)
    }

    // Set up Supabase realtime subscription.
    // events → checkin_events (dictionary §3.12). The `race_id` filter column
    // keeps its name on checkin_events (unlike the roster table below). The
    // realtime payload is ignored — any change just triggers a debounced refetch.
    const channel = supabase
      .channel(`race-${raceId}-events`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'checkin_events',
          filter: `race_id=eq.${raceId}`,
        },
        (payload) => {
          console.log('Realtime event received:', payload);
          // Use debounced version to batch rapid events (reduces refetches by 90%)
          // During burst activity (10 check-ins in 5 seconds), this batches into 2-3 fetches
          fetchRunnersDebounced();
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    // Also subscribe to the roster table so DNS/status changes propagate
    // immediately (the events subscription only covers check-in activity, not
    // runner-record edits).
    // runners → runner_records (dictionary §3.12), and the filter COLUMN renames
    // too: runner_records references the race via `event_id`, not `race_id`
    // (the FK-suffix asymmetry). So the filter is `event_id=eq.${raceId}`.
    const runnersChannel = supabase
      .channel(`race-${raceId}-runners`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'runner_records',
          filter: `event_id=eq.${raceId}`,
        },
        () => {
          fetchRunnersDebounced();
        }
      )
      .subscribe();

    runnersChannelRef.current = runnersChannel;

    // Cleanup function
    return () => {
      // Cancel any pending debounce timeout to prevent stale raceId data
      // This fixes the bug where old debounced fetches could fire after raceId changes
      if (debounceCancelRef.current) {
        debounceCancelRef.current();
        debounceCancelRef.current = null;
      }

      // Clear polling interval
      if (pollingIntervalRef.current !== null) {
        window.clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Unsubscribe from realtime channels
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }

      if (runnersChannelRef.current) {
        supabase.removeChannel(runnersChannelRef.current);
        runnersChannelRef.current = null;
      }
    };
  }, [raceId, enabled, autoRefresh, fetchRunnersImmediate, fetchRunnersDebounced]);

  return {
    runners,
    feedEvents,
    loading,
    lastUpdated,
    autoRefresh,
    setAutoRefresh,
    refetch,
  };
}

